import React, { useEffect, useMemo, useState } from "react";
import Modal, { CommonModalProps } from "..";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Divider,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UploadCard, { UploadCardHandle, UploadContext } from "../../Cards/UploadCard/upload-card";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { useSelector } from "react-redux";
import {
  AddArtwork,
  GetArtworkContentId,
  GetBroadcasterList,
  GetCulturesList,
  GetSelectedTitle,
  SetArtworkContentId,
} from "../../../Redux/Reducers/redux-slice-bundle-maker";
import { assetService, cacheService, setupService } from "../../../Core/Api/services";
import { DbCultureInfos } from "../../../Core/Api/Types/db-culture-infos";
import {
  generateArtworkCultureIdsMetadata,
  generateBroadcasterIdMetadata,
  generateLanguagePictureMetadata,
} from "../../../Redux/Utils/Bundlemaker/bundlemaker-utils";
import { IDbBaseMetadataValueInfos } from "../../../Core/Api/Types/i-db-base-metadata-value-infos";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { UploadCardFormatDto } from "../../../Core/Api/Types/db-format-infos";
import { useDispatch } from "react-redux";
import { PlatformLanguageCode } from "../../../Core/Api/Types/platform-language-code";
import { CreateNewDbMetadataValue } from "../../../Core/Utils/asset-object-utils";
import { BaseMetadataType } from "../../../Core/Api/Types/base-metadata-type";
import { DbDeliveryBroadcasterInfos } from "../../../Core/Api/Types/db-delivery-broadcaster-infos";
import { useTranslation } from "react-i18next";

export type BundlemakerUploadArtworkProps = {
  closeModal: () => void;
  cultureId: number | null;
  languageId: number | null;
  broadcasterId: number | null;
  isTitleContext?: boolean;
};

export type ModalBundlemakerUploadArtworkProps = CommonModalProps<BundlemakerUploadArtworkProps>;

const ModalBundlemakerUploadArtwork = React.forwardRef<any, ModalBundlemakerUploadArtworkProps>((props, ref) => {
  const { t } = useTranslation(); // Initialize translation hook

  const uploadCardIndex: number = 0;
  const uploadCardRef = React.useRef<UploadCardHandle>(null);

  const { closeModal, cultureId, languageId, broadcasterId, isTitleContext = false } = props;
  const enqueueCustomSnackbar = useCustomSnackbar();
  const dispatch = useDispatch();

  //------Select store values------

  const cultures = useSelector(GetCulturesList);
  const parentId = useSelector(GetArtworkContentId);
  const title = useSelector(GetSelectedTitle);
  const broadcasters = useSelector(GetBroadcasterList);

  //------local state------

  const [selectedCultures, setSelectedCultures] = useState<DbCultureInfos[]>([]);
  const [selectedLanguageId, setSelectedLanguage] = useState<number>(-1);
  const [selectedPlatform, setSelectedPlatform] = useState<DbDeliveryBroadcasterInfos | null>(null);
  const [objectId, setObjectId] = useState<number>(0);
  const [formats, setFormats] = useState<UploadCardFormatDto[]>([]);
  const [format, setFormat] = useState<UploadCardFormatDto>();
  const [fileName, setFileName] = useState<string>("");
  const [shouldUpload, setShouldUpload] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  //------Memoized values derived from state------

  const languages = useMemo(() => {
    const result: PlatformLanguageCode[] = [];
    cultures &&
      cultures.length > 0 &&
      cultures.forEach((c) => {
        c.languagesMap &&
          c.languagesMap.length > 0 &&
          c.languagesMap.forEach((l) => {
            !result.find((x) => x.id === l.id) && result.push(l);
          });
      });
    return result;
  }, [cultures]);

  //------Effects to init modal state------

  React.useEffect(() => {
    title &&
      title.id &&
      setupService.GetFormatsByLayerAndType(title.id).then(function (formats) {
        formats.forEach((format) => {
          // override cause we don't want user to be able to delete file after uploading it in this modal
          format.userHasRighToDelete = false;
        });
        setFormats(formats);
      });
  }, [title?.id]);

  useEffect(() => {
    if (cultureId) {
      const cultureToSet = cultures.find((x) => x.id === cultureId);
      cultureToSet && setSelectedCultures([cultureToSet]);
    }
  }, [cultureId]);

  useEffect(() => {
    if (languageId) {
      setSelectedLanguage(languageId);
    }
  }, [languageId]);

  useEffect(() => {
    if (broadcasterId) {
      setSelectedPlatform(broadcasters.find((x) => x.id == broadcasterId) || null);
    }
  }, [broadcasterId]);

  //------Data generation / api calls to create assets------

  const generateMetadata = () =>
    [
      generateArtworkCultureIdsMetadata(selectedCultures.map((x) => x.id)),
      generateLanguagePictureMetadata(languages.find((l) => l.id === selectedLanguageId)),
      selectedPlatform && generateBroadcasterIdMetadata(selectedPlatform),
    ].filter((x) => x);

  const generateImageAsset = (titleId: number, parentId: number, titleName: string) =>
    new DbAssetObjectInfos({
      isNew: true,
      hidden: false,
      isDeleted: false,
      type: ObjectType.Image,
      objectType: ObjectType.Image,
      layer: ObjectLayer.Asset,
      status: ObjectStatus.Accepted,
      name: fileName,
      parentObjectId: parentId,
      parentTitleId: titleId,
      classification: ObjectClass.Mezz,
      allParentObjectIds: [titleId, parentId],
      metadata: (generateMetadata() as IDbBaseMetadataValueInfos[]).filter((x) => x !== null),
      parentTitleName: titleName || null,
      parentTitle: title || null,
      trash: false,
    });

  const generateArtworkContent = (titleId: number) =>
    new DbAssetObjectInfos({
      isNew: true,
      hidden: false,
      isDeleted: false,
      type: ObjectType.GraphSkeleton,
      objectType: ObjectType.GraphSkeleton,
      layer: ObjectLayer.Content,
      status: ObjectStatus.New,
      name: "Artwork",
      metadata: [
        CreateNewDbMetadataValue(
          cacheService.GetMetadata().find((x) => x.tagName === "contentType")!.id,
          BaseMetadataType.DbStringMetadataValueInfos,
          "contentType",
          "Artwork"
        ),
      ],
      parentObjectId: titleId,
      parentTitleId: titleId,
      classification: ObjectClass.Group,
      allParentObjectIds: [titleId],
      trash: false,
    });

  const createArtworkContent = () => {
    title?.id &&
      assetService.CreateStandalone({ objs: [generateArtworkContent(title.id)] }).then((response) => {
        if (response && response.createdObjects && response.createdObjects[0] && response.createdObjects[0].id) {
          dispatch(SetArtworkContentId(response.createdObjects[0].id));
          setShouldUpload(true);
        } else {
          setIsUploading(false);
          enqueueCustomSnackbar(response.message || t("alert:error.artworkCreating"), "error");
        }
      });
  };

  const createImageAsset = () => {
    assetService.CreateStandalone({ objs: [generateImageAsset(title!.id, parentId, title!.name)] }).then((res) => {
      if (res.success && res.createdObjects && res.createdObjects[0]) {
        setObjectId(res.createdObjects[0].id);
      } else {
        setIsUploading(false);
        enqueueCustomSnackbar(res.message || t("alert:error.assetObjectCreating"), "error");
      }
    });
  };

  //------Handle user actions------

  const handleUpload = () => {
    // no parent id mean we need to create arwork content before uploading
    if (parentId > 0) {
      uploadCardRef.current?.performUpload();
    } else {
      createArtworkContent();
    }
  };

  const handleSave = () => {
    setIsUploading(true);
    // if no parent id, create artwork content first
    if (title && title.id && title.name && parentId > 0) {
      createImageAsset();
    } else {
      createArtworkContent();
    }
  };

  const handleDropFile = (file: File[]) => {
    const name = file[0].name.split(".")[0];
    const extension = file[0].name.split(".")[file[0].name.split(".").length - 1];
    const correspondingFormat = formats.find((x) => {
      let result = false;
      Object.keys(x.acceptsFiles).forEach((k) => {
        if (x.acceptsFiles[k].find((f) => f === `.${extension.toLowerCase()}`)) {
          result = true;
        }
      });
      return result;
    });
    correspondingFormat && setFormat(correspondingFormat);
    name && setFileName(name);
  };

  //------Effects/callback to handle upload chain------

  useEffect(() => {
    // should upload and parent id variables change on artwork content creation
    if (shouldUpload && parentId > 0) {
      createImageAsset();
      setShouldUpload(false);
    }
  }, [shouldUpload, parentId]);

  useEffect(() => {
    // objectId is set on image asset creation, meaning we can perform upload
    if (objectId > 0) {
      handleUpload();
    }
  }, [objectId]);

  //

  const onSuccessUpload = () => {
    setIsUploading(false);
    assetService.GetObjectById(objectId).then((res) => {
      if (res.success && res.graph) {
        dispatch(AddArtwork(res.graph));
        closeModal();
      } else {
        enqueueCustomSnackbar(res.message || t("alert:error.retrievingUploadedArtwork"), "error");
      }
    });
  };

  return (
    <Modal
      width={750}
      closeModal={closeModal}
      title={t("modal:title.artworkManagement")}
      footer={
        <Stack position="relative" direction="row" spacing={2}>
          <Button onClick={() => closeModal()} startIcon={<ArrowBackIcon />} color="inherit">
            {t("common:cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSave()}
            sx={{ flex: 1 }}
            disabled={selectedCultures.length === 0 || selectedLanguageId === -1 || isUploading || fileName === ""}
          >
            {t("common:save")}
          </Button>
          {isUploading && (
            <CircularProgress
              size={24}
              sx={{
                position: "absolute",
                top: "8px",
                right: "12px",
              }}
            />
          )}
        </Stack>
      }
    >
      <Stack spacing={3}>
        <UploadCard
          title={t("modal:modalBundlemakerUploadArtwork.subTitle")}
          ref={uploadCardRef}
          coverPlaceholderIcon={<AddPhotoAlternateIcon />}
          userHasRightToDelete={true}
          context={UploadContext.MediaFile}
          cardIndex={uploadCardIndex}
          lockUpload={true}
          uploadButtonInCard={false}
          onDroppedFiles={handleDropFile}
          onSuccessUpload={onSuccessUpload}
          objectId={objectId}
          format={
            format
              ? format
              : {
                  id: 1,
                  isUploaded: false,
                  maxFileSizeMb: 100,
                  name: "File",
                  userHasRighToDelete: false,
                  acceptsFiles: { ["application/octet-stream"]: [".pdf", ".jpg", ".jpeg", ".png", ".lsr"] },
                  description: "Certificate",
                  isUserDataMandatory: false,
                  objectType: ObjectType.Image,
                }
          }
        />
        <Divider sx={{ marginBottom: "24px" }} />
        {cultures && cultures.length > 0 && (
          <>
            <InputLabel htmlFor="select-cultures">
              <Typography variant="subtitle2" fontWeight={600}>
                {t("field:label.associatedCounties")}
              </Typography>
            </InputLabel>
            <Autocomplete
              value={selectedCultures}
              onChange={(e, newValue) => setSelectedCultures(newValue)}
              multiple
              id="select-cultures"
              options={[...cultures].sort((a, b) => (a.englishName || "").localeCompare(b.englishName || ""))}
              getOptionLabel={(option) => option.englishName || ""}
              defaultValue={[]}
              filterSelectedOptions
              fullWidth
              renderInput={(params) => (
                <OutlinedInput
                  rows={1}
                  {...params.InputProps}
                  inputProps={params.inputProps}
                  required
                  sx={{ width: "100%", overflowY: "scroll", maxHeight: "50px" }}
                  placeholder={t("field:placeholder.selectCountries")}
                />
              )}
            />
          </>
        )}
        {languages && languages.length && (
          <>
            <InputLabel htmlFor="select-language">
              <Typography variant="subtitle2" fontWeight={600}>
                {t("common:language")}
              </Typography>
            </InputLabel>
            <Select
              onChange={(e) => setSelectedLanguage(e.target.value as number)}
              value={selectedLanguageId}
              size="small"
              id="select-language"
              sx={{ width: "100%" }}
            >
              {languages.map((l) => (
                <MenuItem value={l.id || undefined}>{l.label}</MenuItem>
              ))}
            </Select>
          </>
        )}
        {isTitleContext && broadcasters && broadcasters.length > 0 && (
          <>
            <InputLabel htmlFor="select-broadcasters">
              <Typography variant="subtitle2" fontWeight={600}>
                {t("common:platform")}
              </Typography>
            </InputLabel>
            <Autocomplete
              value={selectedPlatform}
              onChange={(e, newValue) => setSelectedPlatform(newValue!)}
              id="select-platform"
              options={broadcasters}
              getOptionLabel={(option) => option.name || ""}
              filterSelectedOptions
              fullWidth
              renderInput={(params) => (
                <OutlinedInput
                  rows={1}
                  {...params.InputProps}
                  inputProps={params.inputProps}
                  required
                  sx={{ width: "100%", overflowY: "scroll", maxHeight: "50px" }}
                  placeholder={t("field:placeholder.selectPlatform")}
                />
              )}
            />
          </>
        )}
      </Stack>
    </Modal>
  );
});

export default ModalBundlemakerUploadArtwork;
